import { useConfirmDialog, useError, useToast } from '@ztechapps/common.hook'
import { formatMoney } from '@ztechapps/common.utils'
import { COMMITTED_ORDER_STATUSES, isOnAccountTerm } from '@ztechapps/order.common'
import type { OrderStatusEnum } from '@ztechapps/common.contract'
import { OrderPaymentStatusEnum } from '@ztechapps/common.contract'
import { useMarkOrderSettled } from './useMarkOrderSettled'

export type UseOrderSettleArgs = {
  orderId?: string
  balance?: number
  paymentTerms?: string
  paymentStatus?: OrderPaymentStatusEnum
  status?: OrderStatusEnum
  // Refresh the affected order/list after the settlement is recorded.
  onSuccess?: () => void | Promise<void>
}

/**
 * Orchestrates the on-account settlement (guard + confirm dialog + mutation + toast) for the
 * balance-row Settled button. `canSettle` is true only for a committed order (a quotation /
 * in-cart / cancelled order has nothing to settle) on an on-account term with an open balance
 * that is not already written off (Not Receivable), since settling would contradict the write-off.
 */
export const useOrderSettle = ({
  orderId,
  balance,
  paymentTerms,
  paymentStatus,
  status,
  onSuccess,
}: UseOrderSettleArgs) => {
  const [markOrderSettled, { loading, error }] = useMarkOrderSettled()
  const { showSuccess } = useToast()
  useError(error)

  const hasOpenBalance = !!balance && balance > 0
  const isCommitted = !!status && COMMITTED_ORDER_STATUSES.includes(status)
  const canSettle =
    isCommitted &&
    isOnAccountTerm(paymentTerms) &&
    hasOpenBalance &&
    paymentStatus !== OrderPaymentStatusEnum.notReceivable

  const { onConfirm } = useConfirmDialog({
    title: 'Pay on account?',
    text: `This records an on-account settlement of ${formatMoney(
      balance
    )} — use it once the customer's payment is confirmed. The order becomes Fully Paid and leaves accounts receivable.`,
    icon: 'info',
    buttons: { confirm: { text: 'Yes, pay on account' } },
    confirmed: async () => {
      await markOrderSettled({ orderId: orderId ?? '' })
      showSuccess({ message: 'Order settled on account.' })
      await onSuccess?.()
    },
  })

  return { canSettle, onSettle: onConfirm, loading }
}
